<cnt-navbar></cnt-navbar>

<main class="pt-24 md:pt-28 pb-24 min-h-screen bg-cream">
  <div class="px-[2%]">
    <div class="max-w-2xl mx-auto px-4 md:px-8">
      <h1 class="font-headline font-bold text-dark-text tracking-tight text-3xl md:text-4xl mb-2">
        {{ editing ? 'Edit point of interest' : 'Suggest a point of interest' }}
      </h1>
      <p class="text-muted-text font-body mb-8">Help fellow RVers find dump stations, rest areas, trails, and more.</p>

      @if (loading) {
        <div class="flex items-center gap-2 text-muted-text py-12 justify-center">
          <span aria-hidden="true" class="material-symbols-outlined animate-spin">progress_activity</span>
          Loading…
        </div>
      } @else {
        <!-- Category / activity selector -->
        <div class="mb-8">
          <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold mb-3 block">
            What kind of point of interest is this? (Required)
          </span>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
            @for (cat of categoryOptions; track cat.slug) {
              <button
                type="button"
                (click)="flags[cat.field!] = !flags[cat.field!]"
                class="flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-body transition-colors text-left"
                [ngClass]="
                  flags[cat.field!]
                    ? 'border-trinidad bg-trinidad/5 text-trinidad'
                    : 'border-dark-text/15 text-dark-text hover:border-trinidad/50'
                "
              >
                <span
                  aria-hidden="true"
                  class="material-symbols-outlined text-lg shrink-0"
                  [cntSymbol]="cat.icon"
                ></span>
                <span class="truncate">{{ cat.label }}</span>
              </button>
            }
          </div>
          <div class="mt-3">
            <label class="block text-sm font-body font-bold text-dark-text mb-1">Something else? Describe it</label>
            <input
              aria-label="Describe the activity"
              type="text"
              [(ngModel)]="customActivity"
              maxlength="120"
              name="customActivity"
              class="w-full px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
              placeholder="e.g. Disc golf course"
            />
          </div>
          @if (!hasAtLeastOneCategory) {
            <p class="text-trinidad text-xs font-body mt-2">
              A point of interest category or activity must be selected.
            </p>
          }
        </div>

        <!-- Name / description -->
        <div class="mb-6">
          <label class="block text-sm font-body font-bold text-dark-text mb-1">Name</label>
          <input
            aria-label="Name"
            type="text"
            [(ngModel)]="name"
            maxlength="120"
            name="name"
            class="w-full px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
          />
        </div>
        <div class="mb-8">
          <label class="block text-sm font-body font-bold text-dark-text mb-1">Description</label>
          <textarea
            aria-label="Description"
            [(ngModel)]="description"
            name="description"
            rows="4"
            class="w-full px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
          ></textarea>
        </div>

        <!-- Address -->
        <div class="mb-6">
          <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold mb-3 block"
            >Location</span
          >
          <div class="relative mb-2">
            <input
              aria-label="Street address"
              type="text"
              [(ngModel)]="address"
              name="address"
              (input)="onStreetInput(address)"
              placeholder="Street address"
              class="w-full px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
            @if (suggestions.length > 0) {
              <div
                class="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-dark-text/15 shadow-lg overflow-hidden"
              >
                @for (s of suggestions; track $index) {
                  <button
                    type="button"
                    (click)="selectSuggestion(s)"
                    class="w-full text-left px-4 py-2 text-sm font-body hover:bg-cream/60"
                  >
                    {{ s.title }}
                  </button>
                }
              </div>
            }
          </div>
          <div class="grid grid-cols-3 gap-2 mb-4">
            <input
              aria-label="City"
              type="text"
              [(ngModel)]="city"
              name="city"
              placeholder="City"
              class="px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
            <input
              aria-label="State"
              type="text"
              [(ngModel)]="state"
              name="state"
              placeholder="State"
              class="px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
            <input
              aria-label="ZIP code"
              type="text"
              [(ngModel)]="zipcode"
              name="zipcode"
              placeholder="Zip"
              class="px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
          </div>

          <div class="grid grid-cols-2 gap-2 mb-3">
            <input
              aria-label="Latitude"
              type="number"
              [(ngModel)]="lat"
              name="lat"
              min="-90"
              max="90"
              step="0.000001"
              placeholder="Latitude"
              class="px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
            <input
              aria-label="Longitude"
              type="number"
              [(ngModel)]="lng"
              name="lng"
              min="-180"
              max="180"
              step="0.000001"
              placeholder="Longitude"
              class="px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
          </div>
          <button
            type="button"
            (click)="useMyLocation()"
            [disabled]="locating"
            class="mb-4 inline-flex items-center gap-1.5 text-xs font-body font-bold text-dark-text hover:text-trinidad"
          >
            <span aria-hidden="true" class="material-symbols-outlined text-base">my_location</span>
            {{ locating ? 'Locating…' : 'Use my current location' }}
          </button>

          <cnt-activity-location-map
            [lat]="lat"
            [lng]="lng"
            (positionChange)="onMapPositionChange($event)"
          ></cnt-activity-location-map>
          <p class="text-muted-text text-xs font-body mt-2">Click the map to drop a pin, or drag it to fine-tune.</p>
        </div>

        <!-- Photos -->
        <div class="mb-8">
          <span class="text-trinidad font-label uppercase tracking-[0.14em] text-[0.7rem] font-bold mb-3 block"
            >Photos</span
          >
          <div class="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
            @for (photo of existingPhotos; track $index) {
              @if (!removedPhotoIndexes.has($index)) {
                <div class="relative aspect-square rounded-lg overflow-hidden">
                  <img [src]="photoUrl(photo)" alt="" class="w-full h-full object-cover" />
                  <button
                    aria-label="Remove photo"
                    type="button"
                    (click)="removeExistingPhoto($index)"
                    class="absolute top-1 right-1 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"
                  >
                    <span aria-hidden="true" class="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              }
            }
            @for (photo of newPhotos; track $index) {
              <div class="relative aspect-square rounded-lg overflow-hidden">
                <img [src]="photo" alt="" class="w-full h-full object-cover" />
                <button
                  aria-label="Remove photo"
                  type="button"
                  (click)="removeNewPhoto($index)"
                  class="absolute top-1 right-1 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"
                >
                  <span aria-hidden="true" class="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            }
          </div>
          <label
            class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-dark-text/25 text-sm font-body font-bold text-dark-text cursor-pointer hover:border-trinidad"
          >
            <span aria-hidden="true" class="material-symbols-outlined text-lg">add_photo_alternate</span>
            Add photos
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              multiple
              class="hidden"
              (change)="onPhotosPicked($event)"
            />
          </label>
        </div>

        <!-- Contact -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div>
            <label class="block text-sm font-body font-bold text-dark-text mb-1">Phone</label>
            <input
              aria-label="Phone"
              type="tel"
              [(ngModel)]="phone"
              name="phone"
              cntMaskPhone
              class="w-full px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
          </div>
          <div>
            <label class="block text-sm font-body font-bold text-dark-text mb-1">Website</label>
            <input
              aria-label="Website"
              type="url"
              [(ngModel)]="url"
              name="url"
              class="w-full px-4 py-2.5 rounded-lg border border-dark-text/15 font-body focus:outline-none focus:border-trinidad"
            />
          </div>
        </div>

        <!-- Visibility -->
        <label class="flex items-center gap-3 mb-8 cursor-pointer">
          <input
            type="checkbox"
            [ngModel]="!hidden"
            (ngModelChange)="hidden = !$event"
            name="shown"
            class="w-5 h-5 accent-trinidad"
          />
          <span class="text-sm font-body font-bold text-dark-text">Show this point of interest publicly</span>
        </label>

        <div class="flex items-center gap-3">
          <button
            type="button"
            (click)="save()"
            [disabled]="!hasAtLeastOneCategory || saving"
            class="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            @if (saving) {
              <span aria-hidden="true" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
            }
            {{ editing ? 'Save changes' : 'Submit' }}
          </button>
          <a routerLink="/" class="text-sm font-body font-bold text-muted-text hover:text-trinidad">Cancel</a>
        </div>
      }
    </div>
  </div>
</main>

<curbnturf-footer></curbnturf-footer>
